import Phaser from 'phaser';
import GoldButton from 'ui/button';
import SpriteButton from 'ui/spriteButton';
import {
  BLUE_PIECES,
  COLORS,
  RED_PIECES,
  RED_PIECE_RANK_INDEXES
} from 'helpers/constants';
import { getGradeForScore } from 'helpers/scores';
import ALL_ITEMS from 'items/all_items';
import { maybeStoreBattleResults } from 'helpers/player-browser';
import { submitScore, pingDiscord } from 'helpers/server_connection';

const formatSeconds = seconds => {
  const minutes = Math.floor(seconds / 60);
  const secondsOver = seconds % 60;
  const singleDigitSeconds = secondsOver < 10 ? '0' : '';
  return `${minutes}:${singleDigitSeconds}${secondsOver}`;
};

const getCapturedPieceIndex = piece =>
  piece && typeof piece === 'object' ? piece.index : piece;

const getMiniPieceFrame = piece => {
  const pieceIndex = getCapturedPieceIndex(piece);
  const redPieceIndex = RED_PIECES.indexOf(pieceIndex);
  if (redPieceIndex > -1) {
    return RED_PIECE_RANK_INDEXES[redPieceIndex] + 6;
  }
  const bluePieceIndex = BLUE_PIECES.indexOf(pieceIndex);
  if (bluePieceIndex > -1) {
    return bluePieceIndex % 6;
  }
  return 0;
};

const getCapturedBaseX = (pieces, sideOffset) =>
  sideOffset - ((pieces.length - 1) * 10) / 2;

export default class StoryResults extends Phaser.Scene {
  constructor() {
    super('StoryResultsScene');
  }
  init(data) {
    this.done = data.done;
    this.stats = data.stats;
    this.levelData = data.levelData;
    this.levelIndex = data.levelIndex;
    this.scoreLevelId = data.scoreLevelId || data.levelIndex;
    this.scoreLocalKey =
      data.scoreLocalKey ||
      (data.levelIndex != null ? String(data.levelIndex) : '');
    this.scoreMode = data.scoreMode || 'story';
    this.obtainedItems = data.newlyObtainedItems;
    this.highlightButtonByName = data.highlightButtonByName;
    this.isControllerInput = data.isControllerInput;
    this.menuOptions = data.menuOptions;
    this.resultButtons = data.resultButtons || null;
    this.initialButtonName =
      data.initialButtonName ||
      (this.resultButtons && this.resultButtons[0]
        ? this.resultButtons[0].name
        : 'continue');
    this.scale.on('resize', this.resize);
    this.events.on('shutdown', () => {
      this.scale.off('resize', this.resize);
    });
  }
  resize = () => {
    const cam = this.cameras.main;
    const scale = this.game.defaultZoom;
    const centerX = parseInt(cam.centerX);
    // +20 just easier after the graphic changed
    const centerY = parseInt(cam.centerY);

    this.backdropRect = new Phaser.Geom.Rectangle(
      0,
      0,
      window.innerWidth,
      window.innerHeight
    );

    this.backdrop.clear();
    this.backdrop.fillStyle(0x1f272c, 0);
    this.backdrop.fillRectShape(this.backdropRect);

    this.bg.setPosition(centerX, centerY).setScale(scale);
    this.grade
      .setPosition(centerX + 55 * scale, centerY + 55 * scale)
      .setScale(scale);

    this.levelName
      .setPosition(centerX, centerY - 114 * scale)
      .setStyle({ fontSize: `${12 * scale}px` });

    this.defeatedLabel
      .setPosition(centerX, centerY - 52 * scale)
      .setStyle({ fontSize: `${12 * scale}px` });
    this.itemsObtainedLabel
      .setPosition(centerX, centerY - 20 * scale)
      .setStyle({ fontSize: `${12 * scale}px` });
    this.itemNameLabel
      .setPosition(centerX + 5 * scale, centerY - 6 * scale)
      .setStyle({ fontSize: `${12 * scale}px` });
    if (this.newItemSprite) {
      this.newItemSprite
        .setPosition(
          centerX - 2 * scale - Math.floor(this.itemNameLabel.width / 2),
          centerY - 6 * scale
        )
        .setScale(scale);
    }

    this.matchTime
      .setPosition(centerX - 100 * scale, centerY + 23 * scale)
      .setStyle({ fontSize: `${12 * scale}px` });
    this.matchTimeScore
      .setPosition(
        this.matchTime.x + this.matchTime.width + 4,
        centerY + 24 * scale
      )
      .setStyle({ fontSize: `${8 * scale}px` });
    this.numUndosUsed
      .setPosition(centerX - 100 * scale, centerY + 35 * scale)
      .setStyle({ fontSize: `${12 * scale}px` });
    this.numUndosUsedScore
      .setPosition(
        this.numUndosUsed.x + this.numUndosUsed.width + 4,
        centerY + 36 * scale
      )
      .setStyle({ fontSize: `${8 * scale}px` });
    this.numTurns
      .setPosition(centerX - 100 * scale, centerY + 47 * scale)
      .setStyle({ fontSize: `${12 * scale}px` });
    this.numTurnsScore
      .setPosition(
        this.numTurns.x + this.numTurns.width + 4,
        centerY + 48 * scale
      )
      .setStyle({ fontSize: `${8 * scale}px` });
    this.numItemsUsed
      .setPosition(centerX - 100 * scale, centerY + 59 * scale)
      .setStyle({ fontSize: `${12 * scale}px` });
    this.numItemsUsedScore
      .setPosition(
        this.numItemsUsed.x + this.numItemsUsed.width + 4,
        centerY + 60 * scale
      )
      .setStyle({ fontSize: `${8 * scale}px` });

    this.score
      .setPosition(centerX + 30 * scale, centerY + 23 * scale)
      .setStyle({ fontSize: `${12 * scale}px` });

    this.capturedPieces
      .setScale(scale)
      .setPosition(centerX, centerY - 42 * scale);

    this.positionTextResultButtons();
  };
  closeStoryResults = afterClose => {
    this.backdrop.disableInteractive();
    this.done(afterClose);
  };
  positionTextResultButtons = () => {
    if (!this.textResultButtonInstances?.length) {
      return;
    }

    const centerX = parseInt(this.cameras.main.centerX);
    const centerY = parseInt(this.cameras.main.centerY);
    const scale = this.game.defaultZoom;
    const buttonGap = 10 * scale;
    const totalWidth =
      this.textResultButtonInstances.reduce(
        (sum, buttonData) => sum + buttonData.config.width * scale,
        0
      ) +
      buttonGap * (this.textResultButtonInstances.length - 1);
    let currentLeft = centerX - totalWidth / 2;

    this.textResultButtonInstances.forEach(buttonData => {
      const { button, config } = buttonData;
      const buttonWidth = config.width * scale;
      const x = currentLeft + buttonWidth / 2;
      const y =
        centerY + (config.yOffset != null ? config.yOffset : 90) * scale;
      button.resetPosition(x, y);
      currentLeft += buttonWidth + buttonGap;
    });
  };
  createResultButtons = () => {
    const buttonConfigs = (this.resultButtons && this.resultButtons.length
      ? this.resultButtons
      : [
          {
            name: 'continue',
            sprite: 'ui_story_results_continue'
          }
        ]
    ).map(config => ({ ...config }));
    this.textResultButtonInstances = [];
    const useTextButtons = buttonConfigs.every(config => config.text);

    buttonConfigs.forEach((config, index) => {
      const button = useTextButtons
        ? GoldButton(this, {
            text: config.text,
            x: 0,
            y: 0,
            width: config.width,
            height: config.height || 20,
            action: () => {
              this.closeStoryResults(config.action);
            }
          })
        : SpriteButton(this, {
            calcX: scene => scene.cameras.main.centerX,
            calcY: scene =>
              scene.cameras.main.centerY +
              (config.yOffset != null ? config.yOffset : 90 + index * 16) *
                scene.game.defaultZoom,
            sprite: config.sprite,
            action: () => {
              this.closeStoryResults(config.action);
            },
            up: config.up || 0,
            over: config.over || 1,
            down: config.down || 2
          });

      if (useTextButtons) {
        this.textResultButtonInstances.push({
          button,
          config
        });
        this.positionTextResultButtons();
      }

      if (this.menuOptions[config.name]) {
        this.menuOptions[config.name].button = button;
      }
      this[`${config.name}Button`] = button;
    });

    if (this.isControllerInput) {
      this.highlightButtonByName(this.initialButtonName);
    }
  };
  async create() {
    this.scene.bringToTop();
    const scale = this.game.defaultZoom;
    const cam = this.cameras.main;
    const centerX = parseInt(cam.centerX);
    const centerY = parseInt(cam.centerY);
    let totalScore = 0;

    this.backdropRect = new Phaser.Geom.Rectangle(
      0,
      0,
      window.innerWidth,
      window.innerHeight
    );

    this.backdrop = this.add.graphics();
    this.backdrop.lineStyle(3, 0x5b87a1, 1);
    this.backdrop.fillStyle(0x1f272c, 0);
    this.backdrop.fillRectShape(this.backdropRect);
    this.backdrop
      .setInteractive(this.backdropRect, Phaser.Geom.Rectangle.Contains)
      .on('pointerdown', (pointer, x, y, event) => {
        event && event.stopPropagation();
        if (!this.resultButtons) {
          this.closeStoryResults();
        }
      });

    this.bg = this.add
      .sprite(centerX, centerY, 'ui_story_results_bg')
      .setScale(scale);

    this.createResultButtons();

    this.levelName = this.add
      .text(centerX, centerY - 114 * scale, this.levelData.name, {
        fontSize: `${12 * scale}px`,
        color: COLORS.LIGHT_GOLD,
        fontWeight: 'bold',
        fontFamily: 'PixelOperator',
        textAlign: 'center'
      })
      .setOrigin(0.5);

    this.defeatedLabel = this.add
      .text(centerX, centerY - 52 * scale, `defeated`, {
        fontSize: `${12 * scale}px`,
        color: COLORS.LIGHT_BLUE,
        fontWeight: 'bold',
        fontFamily: 'PixelOperator',
        textAlign: 'center'
      })
      .setOrigin(0.5);

    this.itemsObtainedLabel = this.add
      .text(centerX, centerY - 20 * scale, `items obtained`, {
        fontSize: `${12 * scale}px`,
        color: COLORS.LIGHT_BLUE,
        fontWeight: 'bold',
        fontFamily: 'PixelOperator',
        textAlign: 'center'
      })
      .setOrigin(0.5);

    const itemNames = (this.obtainedItems || []).map(
      itemID => ALL_ITEMS[itemID].name
    );
    const itemSprites = (this.obtainedItems || []).map(
      itemID => ALL_ITEMS[itemID].miniSpriteIndex
    );

    const isNewItems = this.obtainedItems && this.obtainedItems.length;

    const itemNameLabelText = isNewItems ? itemNames[0] : 'none';

    this.itemNameLabel = this.add
      .text(centerX + 5 * scale, centerY - 6 * scale, itemNameLabelText, {
        fontSize: `${12 * scale}px`,
        color: COLORS.LIGHT_BLUE,
        fontWeight: 'bold',
        fontFamily: 'PixelOperator',
        textAlign: 'center'
      })
      .setOrigin(0.5);

    if (isNewItems) {
      this.newItemSprite = this.add
        .sprite(
          centerX - 2 * scale - Math.floor(this.itemNameLabel.width / 2),
          centerY - 6 * scale,
          'small_items',
          itemSprites[0]
        )
        .setScale(scale);
    }

    // TIME
    const maxTime = this.stats.parStats
      ? formatSeconds(this.stats.parStats.time)
      : '{Not Set}';

    let timeScoreText;
    if (
      this.stats.parStats &&
      this.stats.timeSeconds <= this.stats.parStats.time
    ) {
      timeScoreText = '+1000';
      totalScore += 1000;
    }

    this.matchTime = this.add
      .text(
        centerX - 100 * scale,
        centerY + 23 * scale,
        `Time:  ${formatSeconds(this.stats.timeSeconds)} / ${maxTime}`,
        {
          fontSize: `${12 * scale}px`,
          color: COLORS.LIGHT_BLUE,
          fontWeight: 'bold',
          fontFamily: 'PixelOperator',
          textAlign: 'right'
        }
      )
      .setOrigin(0, 0.5);

    this.matchTimeScore = this.add
      .text(
        this.matchTime.x + this.matchTime.width + 4,
        centerY + 24 * scale,
        timeScoreText,
        {
          fontSize: `${8 * scale}px`,
          color: COLORS.GOLD,
          fontWeight: 'bold',
          fontFamily: 'oh-forby-othry',
          textAlign: 'right'
        }
      )
      .setOrigin(0, 0.5);

    // UNDOS
    const maxUndos = this.stats.parStats
      ? this.stats.parStats.undos
      : '{Not Set}';

    let undosScoreText;
    if (
      this.stats.parStats &&
      this.stats.undosUsed <= this.stats.parStats.undos
    ) {
      let undoScore = 1000;
      if (this.stats.undosUsed < this.stats.parStats.undos) {
        const diff = this.stats.parStats.undos - this.stats.undosUsed;
        undoScore += 100 * diff;
      }
      undosScoreText = `+${undoScore}`;
      totalScore += undoScore;
    }

    this.numUndosUsed = this.add
      .text(
        centerX - 100 * scale,
        centerY + 35 * scale,
        `Undos:  ${this.stats.undosUsed} / ${maxUndos}`,
        {
          fontSize: `${12 * scale}px`,
          color: COLORS.LIGHT_BLUE,
          fontWeight: 'bold',
          fontFamily: 'PixelOperator',
          textAlign: 'right'
        }
      )
      .setOrigin(0, 0.5);

    this.numUndosUsedScore = this.add
      .text(
        this.numUndosUsed.x + this.numUndosUsed.width + 4,
        centerY + 36 * scale,
        undosScoreText,
        {
          fontSize: `${8 * scale}px`,
          color: COLORS.GOLD,
          fontWeight: 'bold',
          fontFamily: 'oh-forby-othry',
          textAlign: 'right'
        }
      )
      .setOrigin(0, 0.5);

    // TURNS
    const maxTurns = this.stats.parStats
      ? this.stats.parStats.turns
      : '{Not Set}';
    const numTurns = this.stats.turnIndex + 1;
    let turnsScoreText;
    if (this.stats.parStats && numTurns <= this.stats.parStats.turns) {
      let turnsScore = 1000;
      if (numTurns < this.stats.parStats.turns) {
        const diff = this.stats.parStats.turns - numTurns;
        turnsScore += 50 * diff;
      }
      turnsScoreText = `+${turnsScore}`;
      totalScore += turnsScore;
    }

    this.numTurns = this.add
      .text(
        centerX - 100 * scale,
        centerY + 47 * scale,
        `Turns:  ${numTurns} / ${maxTurns}`,
        {
          fontSize: `${12 * scale}px`,
          color: COLORS.LIGHT_BLUE,
          fontWeight: 'bold',
          fontFamily: 'PixelOperator',
          textAlign: 'right'
        }
      )
      .setOrigin(0, 0.5);

    this.numTurnsScore = this.add
      .text(
        this.numTurns.x + this.numTurns.width + 4,
        centerY + 48 * scale,
        turnsScoreText,
        {
          fontSize: `${8 * scale}px`,
          color: COLORS.GOLD,
          fontWeight: 'bold',
          fontFamily: 'oh-forby-othry',
          textAlign: 'right'
        }
      )
      .setOrigin(0, 0.5);

    // ITEMS USED
    const maxItems = this.stats.parStats
      ? this.stats.parStats.items
      : '{Not Set}';
    const itemScoreMax = this.stats.parStats
      ? this.stats.parStats.itemScoreMax ?? this.stats.parStats.items
      : null;

    let itemsScoreText;
    if (this.stats.parStats && this.stats.itemsUsed <= itemScoreMax) {
      itemsScoreText = '+1000';
      totalScore += 1000;
    }

    this.numItemsUsed = this.add
      .text(
        centerX - 100 * scale,
        centerY + 59 * scale,
        `Items:  ${this.stats.itemsUsed} / ${maxItems}`,
        {
          fontSize: `${12 * scale}px`,
          color: COLORS.LIGHT_BLUE,
          fontWeight: 'bold',
          fontFamily: 'PixelOperator',
          textAlign: 'right'
        }
      )
      .setOrigin(0, 0.5);

    this.numItemsUsedScore = this.add
      .text(
        this.numItemsUsed.x + this.numItemsUsed.width + 4,
        centerY + 60 * scale,
        itemsScoreText,
        {
          fontSize: `${8 * scale}px`,
          color: COLORS.GOLD,
          fontWeight: 'bold',
          fontFamily: 'oh-forby-othry',
          textAlign: 'right'
        }
      )
      .setOrigin(0, 0.5);

    // SCORE
    this.score = this.add
      .text(
        centerX + 30 * scale,
        centerY + 23 * scale,
        `SCORE: ${totalScore}`,
        {
          fontSize: `${12 * scale}px`,
          color: COLORS.LIGHT_BLUE,
          fontWeight: 'bold',
          fontFamily: 'PixelOperator',
          textAlign: 'right'
        }
      )
      .setOrigin(0, 0.5);

    const grade = getGradeForScore(totalScore) || 'd';

    this.grade = this.add
      .sprite(
        centerX + 55 * scale,
        centerY + 55 * scale,
        `ui_battle_results_${grade}`
      )
      .setScale(scale);

    const config = `grade_anim_${grade}`;
    this.anims.create({
      key: config,
      frames: this.anims.generateFrameNumbers(`ui_battle_results_${grade}`),
      frameRate: 8,
      yoyo: false,
      repeat: 0
    });
    this.grade.anims.load(config);
    this.grade.anims.play(config);

    //todo - set this once we want to let the user click...
    this.backdrop.setInteractive(
      this.backdropRect,
      Phaser.Geom.Rectangle.Contains
    );

    this.capturedPieces = this.add
      .container()
      .setPosition(centerX, centerY - 42 * scale)
      .setScale(scale);

    const hasRedCaptured = this.stats.captured.red.length > 0;
    const hasBlueCaptured = this.stats.captured.blue.length > 0;
    const redOffset = hasBlueCaptured ? -32 : 0;
    const blueOffset = hasRedCaptured ? 32 : 0;

    // place red captured pieces
    this.stats.captured.red.forEach((piece, index) => {
      const baseX = getCapturedBaseX(this.stats.captured.red, redOffset);
      const baseY = 0;
      const sprite = this.add.sprite(
        baseX + 10 * index,
        baseY,
        'ui_mini_pieces',
        getMiniPieceFrame(piece)
      );
      this.capturedPieces.add(sprite);
    });
    // place blue capured pieces
    this.stats.captured.blue.forEach((piece, index) => {
      const baseX = getCapturedBaseX(this.stats.captured.blue, blueOffset);
      const baseY = 0;
      const sprite = this.add.sprite(
        baseX + 10 * index,
        baseY,
        'ui_mini_pieces',
        getMiniPieceFrame(piece)
      );
      this.capturedPieces.add(sprite);
    });

    const localBattleResult = maybeStoreBattleResults(
      this.scoreLocalKey,
      totalScore,
      grade,
      this.levelData.name
    );
    try {
      const userID = window.localStorage.getItem('chesslike-userid');
      const scoreResult = await submitScore(
        userID,
        localStorage.getItem('chesslike-steamTicket'),
        this.scoreLevelId,
        this.scoreMode,
        totalScore,
        this.stats.timeSeconds,
        this.stats.turnIndex + 1,
        this.stats.undosUsed,
        this.stats.itemsUsed
      );
      const rankSuffix =
        scoreResult.position === 1
          ? 'st'
          : scoreResult.position === 2
          ? 'nd'
          : scoreResult.position === 3
          ? 'rd'
          : 'th';
      if (scoreResult.attemptIsInTop5 && localBattleResult.bestChanged) {
        pingDiscord(
          `**${
            this.game.player.name
          }** just made the leaderboard on level: ${this.levelData.name ||
            this.levelIndex}!
          ${scoreResult.position}${rankSuffix} place!
          Score: ${totalScore}
          Grade: ${grade}`
        );
      }
    } catch (error) {
      console.error('Error submitting score:', error);
    }
  }
}
